import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import {
  findIsraelSettlementByWire,
  israelSettlementCount,
  israelSettlementDisplayLabel,
  israelSettlementWireValue,
  searchIsraelSettlements,
  settlementOptionLabel,
} from '../utils/israelSettlements';
import formStyles from './DynamicRecordForm.module.css';
import styles from './IsraelSettlementCitySelect.module.css';

type IsraelSettlementCitySelectProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  mandatory?: boolean;
  theme?: 'dark' | 'light';
  rowClassName?: string;
};

export function IsraelSettlementCitySelect({
  id,
  label,
  value,
  onChange,
  disabled = false,
  readOnly = false,
  mandatory = false,
  theme = 'dark',
  rowClassName,
}: IsraelSettlementCitySelectProps) {
  const { t, language } = useI18n();
  const listId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const displayValue = useMemo(
    () => israelSettlementDisplayLabel(value, language),
    [language, value],
  );

  const inputValue = open ? draft : displayValue;

  const options = useMemo(
    () => searchIsraelSettlements(open ? draft : value, language),
    [draft, language, open, value],
  );

  const closeMenu = useCallback(() => {
    setOpen(false);
    setDraft('');
    setActiveIndex(0);
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onDocPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target;
      if (!(target instanceof Node) || !wrapRef.current?.contains(target)) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', onDocPointerDown);
    document.addEventListener('touchstart', onDocPointerDown);
    return () => {
      document.removeEventListener('mousedown', onDocPointerDown);
      document.removeEventListener('touchstart', onDocPointerDown);
    };
  }, [closeMenu, open]);

  const pickSettlement = useCallback(
    (wire: string) => {
      onChange(wire);
      closeMenu();
    },
    [closeMenu, onChange],
  );

  const onInputFocus = () => {
    if (disabled || readOnly) {
      return;
    }
    setDraft(displayValue);
    setOpen(true);
    setActiveIndex(0);
  };

  const onInputChange = (next: string) => {
    if (disabled || readOnly) {
      return;
    }
    setDraft(next);
    setOpen(true);
    setActiveIndex(0);
    const exact = findIsraelSettlementByWire(next);
    if (exact) {
      onChange(israelSettlementWireValue(exact));
    } else {
      onChange(next);
    }
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      setDraft(displayValue);
      setOpen(true);
      return;
    }
    if (!open || options.length === 0) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, options.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const picked = options[activeIndex];
      if (picked) {
        pickSettlement(israelSettlementWireValue(picked));
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu();
    }
  };

  const inputClass = [formStyles.textInput, styles.input].filter(Boolean).join(' ');

  const menuClass = [styles.menu, theme === 'light' ? styles.menuLight : ''].filter(Boolean).join(' ');
  const hintClass = [styles.hint, theme === 'light' ? styles.hintLight : ''].filter(Boolean).join(' ');

  return (
    <div className={[rowClassName ?? formStyles.fieldRow, styles.wrap].join(' ')} ref={wrapRef}>
      <label className={formStyles.inputLabel} htmlFor={id}>
        {label}
        {mandatory ? <span className={formStyles.mandatoryAsterisk} aria-hidden="true"> *</span> : null}
      </label>
      <input
        id={id}
        className={inputClass}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-autocomplete="list"
        autoComplete="off"
        value={inputValue}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={t('form.picklist.searchCity')}
        onFocus={onInputFocus}
        onChange={(event) => onInputChange(event.target.value)}
        onKeyDown={onInputKeyDown}
        aria-required={mandatory || undefined}
      />
      {open && options.length > 0 ? (
        <ul id={listId} className={menuClass} role="listbox">
          {options.map((settlement, index) => {
            const wire = israelSettlementWireValue(settlement);
            const optionClass = [styles.option, index === activeIndex ? styles.optionActive : '']
              .filter(Boolean)
              .join(' ');
            return (
              <li key={`${settlement.id ?? 'x'}-${wire}`} role="presentation">
                <button
                  type="button"
                  className={optionClass}
                  role="option"
                  aria-selected={index === activeIndex}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => pickSettlement(wire)}
                >
                  {settlementOptionLabel(settlement, language)}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      <p className={hintClass}>
        {t('form.picklist.citySettlementHint', { count: String(israelSettlementCount()) })}
      </p>
    </div>
  );
}
